import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Globe, Lock, Crown, Share2, Settings, LogOut,
  UserPlus, Users, MessageCircle, Trash2, Trophy
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { getCurrentUser } from '../../profile';
import { useSquads } from '../hooks/useSquads';
import { btn } from '../../utils/buttonStyles';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import SquadCreateEdit from './SquadCreateEdit';
import ConfirmModal from '../../components/ConfirmModal';
import ShareModal from '../../components/common/ShareModal';
import { isSquadManager, roleLabel } from '../utils/squadRoles';

/**
 * SquadSummaryHandler - Self-Contained Squad Header Component
 *
 * Handles all squad header/summary functionality:
 * - Squad header display (title, description, stats, background image)
 * - Share functionality with modal
 * - Edit functionality with modal (admin only)
 * - Join/Leave squad actions
 * - Background image management
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance (required)
 *
 * This component is fully self-contained and manages all its own state.
 */

const SquadSummaryHandler = ({ voProvider, hideTournaments = false }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Get group API functions
  const { joinGroup, leaveGroup, updateGroup, deleteGroup } = useSquads(false);

  const currentUser = getCurrentUser();

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);

  // Leave modal state
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Force re-render when the provider refreshes group data (e.g. after image update).
  // Without this, refreshGroup() updates provider internals but React never re-reads getGroup().
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!voProvider) return;
    return voProvider.addStateChangeListener((changeType) => {
      if (changeType === 'group') forceUpdate(n => n + 1);
    });
  }, [voProvider]);

  // Guard: Return null if voProvider not available
  if (!voProvider) {
    return null;
  }

  // Get data from VO
  const squad = voProvider.getGroup();
  const squadId = squad?.id ?? voProvider.vo?.groupId;
  const isCreator = squad && currentUser && String(currentUser.id) === squad.creatorId;

  /**
   * Handle join group
   */
  const handleJoinGroup = async () => {
    const result = await joinGroup(squadId);
    if (result.success) {
      if (result.pending) {
        toast.success('Join request sent! Waiting for admin approval.');
      } else {
        toast.success('Successfully joined squad!');
      }
      // Reinitialize provider to refresh group data
      window.location.reload();
    } else {
      toast.error('Failed to join squad. Please try again.');
    }
  };

  /**
   * Handle leave group - show modal
   */
  const handleLeaveGroup = () => {
    setShowLeaveModal(true);
  };

  /**
   * Confirm leave group
   */
  const confirmLeaveGroup = async () => {
    setLeaveLoading(true);
    const result = await leaveGroup(squadId);
    if (result.success) {
      toast.success('You have left the squad');
      navigate('/squads');
    } else {
      toast.error('Failed to leave squad. Please try again.');
    }
    setLeaveLoading(false);
    setShowLeaveModal(false);
  };

  /**
   * Handle edit squad - open modal
   */
  const handleEditGroup = () => {
    setShowEditModal(true);
  };

  /**
   * Handle delete group - show confirmation modal
   */
  const handleDeleteGroup = () => {
    setShowDeleteModal(true);
  };

  /**
   * Confirm delete group
   */
  const confirmDeleteGroup = async () => {
    setDeleteLoading(true);
    const result = await deleteGroup(squadId);
    setDeleteLoading(false);
    if (result.success) {
      setShowDeleteModal(false);
      navigate('/squads');
    } else {
      toast.error('Failed to delete squad. Please try again.');
    }
  };

  /**
   * Handle successful squad update
   */
  const handleEditSuccess = async () => {
    setShowEditModal(false);
    // Refresh squad data by reinitializing the provider
    if (voProvider) {
      try {
        await voProvider.refreshGroup();
        // Background image updates automatically via squad?.backgroundImage after refresh
      } catch (error) {
        console.error('Error refreshing squad data:', error);
        // Fallback to reload if refresh fails
        window.location.reload();
      }
    }
  };

  /**
   * Navigate to this squad's tournament list
   */
  const handleViewTournaments = () => {
    const sportTypeId = squad?.sportTypeId || squad?.sportType?.id;
    navigate(`/squads/${squadId}/tournaments`, {
      state: {
        ownerName: squad?.name || 'Squad',
        ...(sportTypeId ? { sportTypeId: String(sportTypeId) } : {}),
      },
    });
  };

  /**
   * Handle share
   */
  const handleShare = () => {
    setShowShareModal(true);
  };


  return (
    <>
      {/* Squad Header */}
      <div className={`${theme.card} rounded-2xl mb-6 border ${theme.border} overflow-hidden relative`}>
        {/* Background Image */}
        {squad?.backgroundImage && (
          <div className="absolute inset-0 z-0 overflow-hidden">
            <img
              src={resolveImageUrl(squad?.backgroundImage)}
              alt="Squad background"
              className="w-full h-full object-cover object-top opacity-60"
            />
            {/* Gradient overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40" />
          </div>
        )}

        <div className={`relative z-10 ${squad?.backgroundImage ? 'p-8 min-h-[400px]' : 'p-4 sm:p-6'}`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
            <div className="flex-1 min-w-0">
              {/* Title with Privacy Icon */}
              <div className="flex items-center gap-x-2 mb-2">
                <h1 className={`text-2xl sm:text-3xl font-bold ${squad?.backgroundImage ? 'text-white drop-shadow-lg' : theme.text}`}>
                  {squad?.name || 'Squad'}
                </h1>
                {squad?.privacy === 'private' ? (
                  <Lock className={`w-5 h-5 flex-shrink-0 ${squad?.backgroundImage ? 'text-white/80' : 'text-gray-500'}`} />
                ) : (
                  <Globe className={`w-5 h-5 flex-shrink-0 ${squad?.backgroundImage ? 'text-green-300' : 'text-green-500'}`} />
                )}
              </div>

              {/* Description */}
              <p className={`text-base mb-3 max-w-2xl ${squad?.backgroundImage ? 'text-white/90 drop-shadow' : theme.textSecondary}`}>
                {squad?.description || 'No description available'}
              </p>

              {/* Stats — compact two-line layout on mobile */}
              <div className={`flex flex-wrap gap-x-4 gap-y-1 text-sm mb-3 ${squad?.backgroundImage ? 'text-white/80' : theme.textSecondary}`}>
                <div className="flex items-center gap-x-1">
                  <Users className="w-4 h-4" />
                  <span>{squad?.memberCount || 0} {squad?.memberCount === 1 ? 'member' : 'members'}</span>
                </div>
                <div className="flex items-center gap-x-1">
                  <MessageCircle className="w-4 h-4" />
                  <span>{squad?.postCount || 0} posts</span>
                </div>
                <div className="flex items-center gap-x-1">
                  <Crown className="w-4 h-4" />
                  <span>Created by {squad?.creatorUsername || 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{squad?.createdAt ? new Date(squad.createdAt).toLocaleDateString() : 'Unknown'}</span>
                </div>
              </div>

              {/* Badges row */}
              <div className="flex items-center flex-wrap gap-2">
                {(squad?.sportType?.sportName || squad?.category) && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${squad?.backgroundImage ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-800'}`}>
                    {squad?.sportType?.sportName || squad?.category}
                  </span>
                )}
                {squad?.userRole && (
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    squad?.backgroundImage
                      ? 'bg-white/20 text-white'
                      : isSquadManager(squad.userRole)
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                  }`}>
                    {isSquadManager(squad.userRole) ? (
                      <><Crown className="w-3 h-3 inline mr-1" />{roleLabel(squad.userRole)}</>
                    ) : (
                      roleLabel(squad.userRole)
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons — full width row on mobile, inline on desktop */}
            <div className="flex items-center flex-wrap gap-2">
              {/* Tournaments — visible to members only, hideable via prop */}
              {!hideTournaments && squad?.isMember && (
                <button
                  onClick={handleViewTournaments}
                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-1.5`}
                  title="View squad tournaments"
                >
                  <Trophy className="w-4 h-4" />
                  <span className="hidden sm:inline">Tournaments</span>
                </button>
              )}

              {/* Share Button */}
              <button
                onClick={handleShare}
                className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-1.5`}
                title="Share"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>

              {/* Member-specific buttons */}
              {squad?.isMember ? (
                <>
                  {!isSquadManager(squad.userRole) && (
                    <button
                      onClick={handleLeaveGroup}
                      className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex items-center gap-1.5`}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Leave</span>
                    </button>
                  )}
                  {isSquadManager(squad.userRole) && (
                    <>
                      <button
                        onClick={handleEditGroup}
                        className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-1.5`}
                      >
                        <Settings className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      {isCreator && (
                        <button
                          onClick={handleDeleteGroup}
                          className={`${btn.base} ${btn.md} ${theme.buttonDanger} flex items-center gap-1.5`}
                          title="Delete Squad"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      )}
                    </>
                  )}
                </>
              ) : squad?.memberStatus === 'pending' ? (
                <button
                  disabled
                  className={`${btn.base} ${btn.lg} ${theme.buttonGhost} flex items-center gap-2 opacity-70 cursor-not-allowed`}
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Request Pending</span>
                </button>
              ) : (
                <button
                  onClick={handleJoinGroup}
                  className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} flex items-center gap-2`}
                >
                  <UserPlus className="w-4 h-4" />
                  <span>{squad?.memberStatus === 'invited' ? 'Accept Invitation' : 'Join Squad'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        entity={{ id: squadId, name: squad?.name, description: squad?.description }}
        entityType="squad"
        urlPattern="/squads/{id}"
        currentUser={currentUser}
      />

      {/* Edit Squad Modal */}
      <SquadCreateEdit
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        squad={squad}
        mode="edit"
      />

      {/* Leave Squad Confirm Modal */}
      <ConfirmModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onConfirm={confirmLeaveGroup}
        title="Leave Squad"
        message={`Are you sure you want to leave "${squad?.name}"? You may need to be re-invited to rejoin.`}
        confirmText="Leave"
        confirmStyle="danger"
        loading={leaveLoading}
      />

      {/* Delete Squad Confirm Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteGroup}
        title="Delete Squad"
        message={`Are you sure you want to permanently delete "${squad?.name}"? This will delete all events, games, and data. This cannot be undone.`}
        confirmText="Delete"
        confirmStyle="danger"
        loading={deleteLoading}
      />
    </>
  );
};

export default SquadSummaryHandler;
